import type { CSSProperties } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';

/**
 * SVG illustrations for `DeliveryRateImprovedModal`'s two hero panels.
 * Ported from trulyinbox-webui; animation keyframes live in `global.css`
 * (per-instance stroke-dash values are set inline via `delayStyle` below).
 */

const WARMUP_GRAPH_VB = { w: 400, h: 160 };
/** Clip height > viewBox h so the spam path / node can extend to cy=170 without cutting the fill animation. */
const WARMUP_WITH_CLIP_H = 178;

/** Upper deliverability curve (S-up), lower spam curve; fill closes between them. */
const PATH_UPPER = 'M 0 102 C 72 102 148 48 238 34 S 352 16 400 22';
/** y=118 matches PATH_WITHOUT_LOWER's end at the "Warmup Started" divider. */
const PATH_LOWER = 'M 0 118 C 100 118 235 158 400 170';
const PATH_FILL = `${PATH_UPPER} L 400 170 C 235 158 100 118 0 118 Z`;

const WARMUP_GRADIENT_ID = 'warmup-deliver-stroke-gradient';
const WARMUP_CLIP_ID = 'warmup-deliver-fill-clip';
const WARMUP_STARTED_CLIP_ID = 'warmup-started-dash-clip';
const WARMUP_STARTED_CLIP_WIDTH = 92;

/** Left panel: time flows left → right; paths end at x=400 (the "Warmup Started" divider). */
const PATH_WITHOUT_UPPER = 'M 0 95 C 100 96 200 98 400 102';
const PATH_WITHOUT_LOWER = 'M 0 108 C 120 110 260 114 400 118';
const PATH_WITHOUT_FILL = `${PATH_WITHOUT_UPPER} L 400 118 C 260 114 120 110 0 108 Z`;
const WITHOUT_WARMUP_FILL_CLIP_ID = 'without-warmup-fill-clip';

/** `--warmup-delay` (seconds) + any extra CSS for an animated element. */
function delayStyle(delay: number, extra?: CSSProperties): CSSProperties {
  return { '--warmup-delay': `${delay}s`, ...extra } as CSSProperties;
}

function nodeStyle(delay: number, color: string): CSSProperties {
  return delayStyle(delay, { fill: color, stroke: 'var(--paper)', strokeWidth: 2 });
}

function strokeDashStyle(length: number, delay: number): CSSProperties {
  return delayStyle(delay, { strokeDasharray: length, strokeDashoffset: length });
}

function useStrokeDashLengths() {
  const upperRef = useRef<SVGPathElement>(null);
  const lowerRef = useRef<SVGPathElement>(null);
  const [dash, setDash] = useState({ upper: 480, lower: 420 });

  useLayoutEffect(() => {
    const upperEl = upperRef.current;
    const lowerEl = lowerRef.current;
    if (!upperEl || !lowerEl) {
      return;
    }
    setDash({ upper: upperEl.getTotalLength(), lower: lowerEl.getTotalLength() });
  }, []);

  return { upperRef, lowerRef, dash };
}

export function WarmupWithoutDeliverabilityGraph() {
  const { upperRef, lowerRef, dash } = useStrokeDashLengths();

  return (
    <div className="relative mt-5 h-56 w-full">
      <svg
        className="block h-52 w-full overflow-visible"
        width="100%"
        height="100%"
        viewBox={`0 0 ${WARMUP_GRAPH_VB.w} ${WARMUP_GRAPH_VB.h}`}
        preserveAspectRatio="none"
        aria-hidden
      >
        <clipPath id={WITHOUT_WARMUP_FILL_CLIP_ID}>
          <rect className="warmup-fill-clip-rect" style={delayStyle(0.1)} x="0" y="0" width={WARMUP_GRAPH_VB.w} height={WARMUP_GRAPH_VB.h} />
        </clipPath>

        <path d={PATH_WITHOUT_FILL} clipPath={`url(#${WITHOUT_WARMUP_FILL_CLIP_ID})`} style={{ fill: 'color-mix(in srgb, var(--bad-500) 12%, transparent)' }} />

        <path ref={upperRef} className="warmup-stroke-draw" d={PATH_WITHOUT_UPPER} fill="none" stroke="var(--bad-500)" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" style={strokeDashStyle(dash.upper, 0)} />
        <path ref={lowerRef} className="warmup-stroke-draw" d={PATH_WITHOUT_LOWER} fill="none" stroke="var(--bad-600)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={strokeDashStyle(dash.lower, 0.12)} />

        <circle className="warmup-node-pop" style={nodeStyle(0.95, 'var(--bad-500)')} cx="400" cy="102" r="5" />
        <circle className="warmup-node-pop" style={nodeStyle(1.05, 'var(--bad-600)')} cx="400" cy="118" r="5" />
      </svg>

      <span className="warmup-label-fade absolute right-4 top-[5.5rem] z-[2] inline-flex h-8 min-w-14 items-center justify-center rounded-full px-3 text-sm font-semibold text-white" style={delayStyle(0.85, { background: 'var(--bad-500)' })}>
        ~45%
      </span>
      <span className="warmup-label-fade pointer-events-none absolute left-0 top-[5.75rem] text-sm font-medium leading-5 text-foreground" style={delayStyle(1)}>
        Deliverability
      </span>
      <span className="warmup-label-fade pointer-events-none absolute bottom-[2.95rem] left-0 text-sm font-medium leading-5 text-foreground" style={delayStyle(1.08)}>
        Spam rate
      </span>
    </div>
  );
}

export function WarmupWithDeliverabilityGraph() {
  const { upperRef, lowerRef, dash } = useStrokeDashLengths();
  const t = 1.6; // stagger start: after the "without" graph's fill + strokes finish

  return (
    <div className="relative mt-5 h-56 w-full">
      <svg className="block h-52 w-full overflow-visible" viewBox={`0 0 ${WARMUP_GRAPH_VB.w} ${WARMUP_GRAPH_VB.h}`} preserveAspectRatio="none" aria-hidden>
        <defs>
          <linearGradient id={WARMUP_GRADIENT_ID} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="var(--bad-500)" />
            <stop offset="45%" stopColor="var(--warn-500)" />
            <stop offset="100%" stopColor="var(--good-600)" />
          </linearGradient>
        </defs>

        <clipPath id={WARMUP_CLIP_ID}>
          <rect className="warmup-fill-clip-rect" style={delayStyle(t + 0.1)} x="0" y="0" width={WARMUP_GRAPH_VB.w} height={WARMUP_WITH_CLIP_H} />
        </clipPath>
        <clipPath id={WARMUP_STARTED_CLIP_ID}>
          <rect x="0" y="0" width={WARMUP_STARTED_CLIP_WIDTH} height={WARMUP_WITH_CLIP_H} />
        </clipPath>

        <path d={PATH_FILL} clipPath={`url(#${WARMUP_CLIP_ID})`} style={{ fill: 'color-mix(in srgb, var(--good-500) 22%, transparent)' }} />

        <path className="warmup-dash-segment-show" style={delayStyle(t)} d={PATH_UPPER} fill="none" stroke={`url(#${WARMUP_GRADIENT_ID})`} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" clipPath={`url(#${WARMUP_STARTED_CLIP_ID})`} />
        <path className="warmup-dash-segment-show" style={delayStyle(t)} d={PATH_LOWER} fill="none" stroke="var(--bad-600)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" clipPath={`url(#${WARMUP_STARTED_CLIP_ID})`} />

        <path ref={upperRef} className="warmup-stroke-draw" d={PATH_UPPER} fill="none" stroke={`url(#${WARMUP_GRADIENT_ID})`} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" style={strokeDashStyle(dash.upper, t)} />
        <path ref={lowerRef} className="warmup-stroke-draw" d={PATH_LOWER} fill="none" stroke="var(--bad-600)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={strokeDashStyle(dash.lower, t + 0.12)} />

        <circle className="warmup-node-pop" style={nodeStyle(t + 0.95, 'var(--good-500)')} cx="400" cy="22" r="5.5" />
        <circle className="warmup-node-pop" style={nodeStyle(t + 1.05, 'var(--good-500)')} cx="400" cy="170" r="5.5" />
      </svg>

      <span
        className="warmup-label-fade absolute -right-2.5 -top-5 inline-flex min-h-8 items-center gap-1 rounded-full px-3 text-sm font-semibold text-white"
        style={delayStyle(t + 0.9, { background: 'var(--good-500)', boxShadow: '0 2px 8px color-mix(in srgb, var(--good-500) 35%, transparent)' })}
      >
        91%
      </span>
      <span className="warmup-label-fade absolute right-0 top-11 text-sm font-medium leading-5" style={delayStyle(t + 1, { color: 'var(--good-600)' })}>
        Deliverability ↑
      </span>
      <span className="warmup-label-fade absolute bottom-6 right-0 text-sm font-medium leading-5" style={delayStyle(t + 1.08, { color: 'var(--bad-600)' })}>
        Spam rate ↓
      </span>
    </div>
  );
}
